import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, ShoppingCart, Coffee, BarChart3, FileDown, Loader2, Filter, PieChart as PieChartIcon } from 'lucide-react';
import cafeService from '../services/cafe';
import usePdfExport from '../hooks/usePdfExport';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>{entry.name}: <span className="font-bold">{formatRupiah(entry.value)}</span></p>
        ))}
      </div>
    );
  }
  return null;
};

const COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
const KATEGORI_COLORS = { MAKANAN: '#F97316', MINUMAN: '#3B82F6', SNACK: '#10B981', LAINNYA: '#8B5CF6' };

const CafeReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const { exporting, exportPdf, contentRef } = usePdfExport({
    filename: 'laporan-penjualan-cafe',
    scale: 3,
    orientation: 'landscape'
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const data = await cafeService.getSalesReport(params);
      setReportData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const summary = reportData?.summary || { totalPenjualan: 0, totalTransaksi: 0 };
  const byCategory = reportData?.byCategory || [];
  const byProduct = reportData?.byProduct || [];
  const daily = reportData?.daily || [];

  // Category chart data
  const categoryChartData = useMemo(() => {
    return byCategory.map(c => ({
      name: c._id,
      total: c.total,
      count: c.count
    }));
  }, [byCategory]);

  // Product chart data (top 10)
  const productChartData = useMemo(() => {
    return byProduct.slice(0, 10).map(p => ({
      name: p._id.nama.length > 20 ? p._id.nama.substring(0, 20) + '...' : p._id.nama,
      fullName: p._id.nama,
      total: p.total,
      quantity: p.quantity
    }));
  }, [byProduct]);

  // Daily trend chart data
  const dailyChartData = useMemo(() => {
    return daily.map(d => ({
      date: d._id,
      total: d.total,
      count: d.count,
      label: format(new Date(d._id), 'dd MMM')
    }));
  }, [daily]);

  return (
    <div className="space-y-4 animate-fade-in" ref={contentRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Laporan Penjualan Cafe</h2>
          <p className="mt-1 text-sm text-gray-500">Laporan penjualan produk cafe berdasarkan kategori dan produk</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => exportPdf('Laporan-Penjualan-Cafe')} disabled={exporting} className="btn-primary text-sm">
            {exporting ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Exporting...</span></> : <><FileDown className="w-4 h-4" /><span>Export PDF</span></>}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="px-4 py-3">
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500">Dari:</label>
              <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="input-field text-sm" />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500">Sampai:</label>
              <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="input-field text-sm" />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-20"><Loader2 className="h-10 w-10 animate-spin text-amber-500" /><span className="mt-4 text-gray-500">Loading report...</span></div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Penjualan</p>
                  <p className="text-xl font-bold text-green-600 mt-1">{formatRupiah(summary.totalPenjualan)}</p>
                </div>
                <div className="p-3 rounded-xl bg-green-50"><DollarSign className="w-6 h-6 text-green-600" /></div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Transaksi</p>
                  <p className="text-xl font-bold text-blue-600 mt-1">{summary.totalTransaksi}</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50"><ShoppingCart className="w-6 h-6 text-blue-600" /></div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Rata-rata per Transaksi</p>
                  <p className="text-xl font-bold text-purple-600 mt-1">
                    {summary.totalTransaksi > 0 ? formatRupiah(Math.round(summary.totalPenjualan / summary.totalTransaksi)) : 'Rp 0'}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-purple-50"><BarChart3 className="w-6 h-6 text-purple-600" /></div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Kategori Produk</p>
                  <p className="text-xl font-bold text-amber-600 mt-1">{byCategory.length}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50"><Coffee className="w-6 h-6 text-amber-600" /></div>
              </div>
            </div>
          </div>

          {/* Category Distribution & Top Products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Category */}
            {categoryChartData.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3 className="section-title flex items-center space-x-2">
                    <PieChartIcon className="w-4 h-4" />
                    <span>Penjualan per Kategori</span>
                  </h3>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryChartData} barSize={50} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `Rp ${(v / 1000).toFixed(0)}k`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fontWeight: 600 }} width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                        {categoryChartData.map((entry, i) => (
                          <Cell key={i} fill={KATEGORI_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Category Detail Table */}
                  <div className="mt-3 space-y-1.5">
                    {categoryChartData.map((cat, idx) => (
                      <div key={cat.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: KATEGORI_COLORS[cat.name] || COLORS[idx % COLORS.length] }} />
                          <span className="text-gray-700 font-medium">{cat.name}</span>
                          <span className="text-xs text-gray-400">({cat.count} item)</span>
                        </div>
                        <span className="font-semibold text-gray-900">{formatRupiah(cat.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Top Products */}
            {productChartData.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3 className="section-title flex items-center space-x-2">
                    <Coffee className="w-4 h-4" />
                    <span>Produk Terlaris</span>
                  </h3>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={productChartData} barSize={30} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `Rp ${(v / 1000).toFixed(0)}k`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const fullName = productChartData.find(p => p.name === label)?.fullName || label;
                            const qty = productChartData.find(p => p.name === label)?.quantity || 0;
                            return (
                              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
                                <p className="text-sm font-semibold text-gray-900">{fullName}</p>
                                <p className="text-sm text-amber-600 font-bold mt-1">{formatRupiah(payload[0].value)}</p>
                                <p className="text-xs text-gray-500">{qty} terjual</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="total" radius={[0, 6, 6, 0]} fill="#F59E0B" />
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Top Products List */}
                  <div className="mt-3 space-y-1.5">
                    {productChartData.map((p, idx) => (
                      <div key={p.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                          <span className="text-gray-700">{p.fullName || p.name}</span>
                          <span className="text-xs text-gray-400">({p.quantity}x)</span>
                        </div>
                        <span className="font-semibold text-gray-900">{formatRupiah(p.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Daily Trend */}
          {dailyChartData.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="section-title flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>Tren Penjualan Harian (30 hari terakhir)</span>
                </h3>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.max(Math.floor(dailyChartData.length / 15), 1)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `Rp ${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const count = payload[0]?.payload?.count || 0;
                          return (
                            <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
                              <p className="text-sm font-semibold text-gray-900">{label}</p>
                              <p className="text-sm text-amber-600 font-bold mt-1">{formatRupiah(payload[0].value)}</p>
                              <p className="text-xs text-gray-500">{count} transaksi</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="total" radius={[4, 4, 0, 0]} fill="#3B82F6">
                      {dailyChartData.map((entry, i) => (
                        <Cell key={i} fill={entry.total > 0 ? '#3B82F6' : '#E5E7EB'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!reportData && (
            <div className="card p-12 text-center">
              <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">Belum ada data penjualan</p>
              <p className="text-gray-400 text-sm mt-1">Lakukan transaksi POS untuk melihat laporan</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CafeReport;
